/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Icon,
  Badge,
  Button,
  Divider,
  Container,
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningTwoIcon, DownloadIcon } from "@chakra-ui/icons";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Certificate from "../components/UserComponents/Certificate";

const VerifyCertificate = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [certData, setCertData] = useState(null);
  const [error, setError] = useState("");
  const certificateRef = useRef(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/certificates/verify/${id}`);
        setCertData(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Certificate fetch error:", err);
        setError("This certificate record could not be found or is invalid.");
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [id]);

  const downloadPDF = async () => {
    const element = certificateRef.current;
    if (!element) return;
    
    // Temporarily adjust styles for perfect PDF capture
    const originalShadow = element.style.boxShadow;
    const originalTransform = element.style.transform;
    element.style.boxShadow = 'none';
    element.style.transform = 'none';

    try {
      const canvas = await html2canvas(element, { 
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff' 
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`SkillUp_Certificate_${id}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      element.style.boxShadow = originalShadow;
      element.style.transform = originalTransform;
    }
  };

  if (loading) {
    return (
      <Flex w="100vw" h="100vh" align="center" justify="center" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text fontWeight="bold" color="gray.600">Verifying Blockchain/DB Record...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error || !certData) {
    return (
      <Flex w="100vw" h="100vh" align="center" justify="center" bg="gray.50" p={4}>
        <Box textAlign="center" maxW="400px" bg="white" p={8} borderRadius="2xl" boxShadow="xl">
          <Icon as={WarningTwoIcon} boxSize={12} color="red.500" mb={4} />
          <Heading size="md" mb={2}>Invalid Certificate</Heading>
          <Text color="gray.600" mb={6}>{error}</Text>
          <Button as={Link} to="/" colorScheme="blue" borderRadius="full">Return Home</Button>
        </Box>
      </Flex>
    );
  }

  const { valid, expired, certificate } = certData;

  return (
    <Box minH="100vh" bg="gray.50" py={12} px={4}>
      <Container maxW="container.lg">
        {/* Verification Status Header */}
        <Flex 
          bg={valid && !expired ? "green.50" : "red.50"} 
          p={4} 
          borderRadius="xl" 
          border="1px solid" 
          borderColor={valid && !expired ? "green.200" : "red.200"}
          align="center" 
          justify="center"
          mb={8}
        >
          {valid && !expired ? (
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="green.500" boxSize={6} />
              <Text fontWeight="bold" color="green.700">
                This is a valid, officially recognized corporate certificate.
              </Text>
            </HStack>
          ) : (
            <HStack spacing={3}>
              <Icon as={WarningTwoIcon} color="red.500" boxSize={6} />
              <Text fontWeight="bold" color="red.700">
                This certificate has expired.
              </Text>
            </HStack>
          )}
        </Flex>

        {/* The Certificate Render */}
        <Flex justify="center" mb={8} w="100%">
          <Certificate 
             studentName={certificate.recipientName}
             courseName={certificate.courseTitle}
             completionDate={new Date(certificate.issueDate).toLocaleDateString()}
             certificateId={certificate.certificateId}
          />
        </Flex>
      </Container>
    </Box>
  );
};

export default VerifyCertificate;
